// PlayerRow.jsx
import React from "react";
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { followPlayer, unfollowPlayer } from '../../../features/scout/scoutProfileSlice';
import { normalizePlayerClass } from '../../../utils/normalizePlayerClass';
import profileIcon from '../../../assets/images/profile.png';
import { openOnRowClick } from '../../../utils/rowClick';

export const PlayerRow = ({ player, dynamicStatColumns = [], getStatValue = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleViewProfile = () => {
    // Use player ID to navigate and pass player data to avoid API call
    const playerId = player._id || player.id || player.name.toLowerCase().replace(/\s+/g, '-');
    navigate(`/scout/player/${playerId}`, {
      state: {
        from: 'teams',
        playerData: player  // Pass full player data to avoid API call
      }
    });
  };

  const handleFollowToggle = (e) => {
    e.stopPropagation();
    const playerId = player._id;
    if (player.isFollowing) {
      dispatch(unfollowPlayer(playerId));
    } else {
      dispatch(followPlayer(playerId));
    }
  };

  // Helper function to safely extract value from MongoDB Decimal128
  const extractDecimalValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'object' && value.$numberDecimal) {
      const parsed = parseFloat(value.$numberDecimal);
      return isNaN(parsed) ? null : parsed;
    }
    if (typeof value === 'number') return isNaN(value) ? null : value;
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  // Extract data from API response structure
  const playerName = player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim() || 'N/A';
  const jerseyNumber = player.jerseyNumber || '-';

  // Handle position - prefer primaryPosition over position
  const position = player.primaryPosition || player.position || 'N/A';

  // Handle class - API uses playerClass
  const className = normalizePlayerClass(player.playerClass || player.class) || 'N/A';

  const height = player.height || 'N/A';
  const weight = extractDecimalValue(player.weight) || player.weight || 'N/A';

  // Handle bat/throw - API uses batsThrows
  const batThrow = player.batsThrows || player.batThrow || 'N/A';

  const profileImage = player.profileImage || profileIcon;

  // Get finalScore from battingStats as playerScore - show "NA" if not available
  const playerScore = (() => {
    const stats = Array.isArray(player.battingStats) ? player.battingStats[0] : player.battingStats;
    const score = stats?.finalScore;
    if (score === null || score === undefined || score === '') return 'NA';
    return score;
  })();

  return (
    <tr onClick={openOnRowClick(handleViewProfile)} className="cursor-pointer hover:bg-slate-800/40 transition border-b border-slate-700">
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-300 text-[10px] sm:text-xs whitespace-nowrap text-center">
        {jerseyNumber}
      </td>

      <td className="px-2 sm:px-3 py-2 sm:py-3">
        <img
          src={profileImage}
          alt="photo"
          className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full border border-slate-500 object-cover"
          onError={(e) => {
            e.target.src = profileIcon;
          }}
        />
      </td>

      <td className="px-2 sm:px-3 py-2 sm:py-3 text-white text-[10px] sm:text-xs whitespace-nowrap">
        {playerName}
      </td>
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-300 text-[10px] sm:text-xs whitespace-nowrap">
        {position}
      </td>
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-400 text-[10px] sm:text-xs whitespace-nowrap">
        {className}
      </td>
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-400 text-[10px] sm:text-xs whitespace-nowrap">
        {height}
      </td>
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-400 text-[10px] sm:text-xs whitespace-nowrap">
        {weight}
      </td>
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-400 text-[10px] sm:text-xs whitespace-nowrap">
        {batThrow}
      </td>
      {/* <td className="px-2 sm:px-3 py-2 sm:py-3 text-cyan-200 font-semibold text-[10px] sm:text-xs whitespace-nowrap">
        {playerScore}
      </td> */}

      {/* Dynamic stat columns */}
      {dynamicStatColumns.map((column, idx) => (
        <td key={`stat-${idx}`} className="px-2 sm:px-3 py-2 sm:py-3 text-gray-400 text-center text-[10px] sm:text-xs whitespace-nowrap">
          {getStatValue ? getStatValue(player, column) : '0'}
        </td>
      ))}

      <td className="px-2 sm:px-3 py-2 sm:py-3 sticky right-0 bg-slate-900/95 z-10">
        <div className="flex flex-row gap-1 sm:gap-2 min-w-[160px] sm:min-w-[180px]">
          <button
            onClick={handleViewProfile}
            className="border border-gray-400 bg-[#2e3337] text-white py-1 px-2 sm:px-3 rounded-md text-[10px] sm:text-xs leading-tight hover:bg-slate-800 transition whitespace-nowrap"
          >
            View profile
          </button>
          <button
            onClick={handleFollowToggle}
            className={`${
              player.isFollowing
                ? 'bg-gray-600 hover:bg-gray-700'
                : 'bg-cyan-500 hover:bg-cyan-400'
            } text-white font-semibold py-1 px-2 sm:px-3 rounded-md text-[10px] sm:text-xs leading-tight transition whitespace-nowrap`}
          >
            {player.isFollowing ? 'Unfollow' : 'Follow'}
          </button>
        </div>
      </td>
    </tr>
  );
};
